import { getClient, type Event } from "@sentry/vue"

// Feedback is submitted after the user stops recording. The public captureFeedback
// API only links the currently active replay and cannot take the stopped replay's ID.
// Send a feedback envelope through the configured SDK transport so its explicit
// replay_id stays linked, and await the response to detect delivery failures.
export async function sendReplayFeedback(replayId: string, message: string, eventId: string, context: Pick<Event, "user" | "tags"> = {}) {
  const client = getClient()
  const transport = client?.getTransport()
  if (!client?.getDsn() || !transport || client.getOptions().enabled === false) {
    throw new Error("Sentry is unavailable")
  }
  const options = client.getOptions()
  let timeout: ReturnType<typeof setTimeout> | undefined
  try {
    const response = await Promise.race([
      transport.send([
        { event_id: eventId, sent_at: new Date().toISOString(), ...(options.tunnel ? { dsn: options.dsn } : {}) },
        [
          [
            { type: "feedback" },
            {
              ...context,
              event_id: eventId,
              timestamp: Date.now() / 1000,
              type: "feedback",
              level: "info",
              environment: options.environment,
              release: options.release,
              contexts: { feedback: { message, replay_id: replayId, source: "manual-replay", name: context.user?.username, contact_email: context.user?.email } },
            },
          ],
        ],
      ]),
      new Promise<never>((_resolve, reject) => {
        // A stalled transport must not keep the modal locked forever. This does
        // not cancel delivery, so retries of the same text keep the same event ID.
        timeout = setTimeout(() => reject(new Error("Feedback request timed out")), 10000)
      }),
    ])
    if (!response?.statusCode || response.statusCode < 200 || response.statusCode >= 300) {
      throw new Error("Sentry did not accept the feedback")
    }
  } finally {
    clearTimeout(timeout)
  }
}
